if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.buttons = this.querySelector('[data-buttons]');
      this.left = this.querySelector('[data-left]');
      this.right = this.querySelector('[data-right]');
      this.dots = this.querySelectorAll('[data-dot]');
      this.thumbnails = this.querySelector('[data-thumbnails]');
      this.number = this.querySelector('[data-number]');


      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.currentIndex = 0;
      this.isPointerDown = false;
      this.startX = 0;
      this.scrollLeftStart = 0;
      this.dragged = false; // Track whether dragging occurred
      this.hasScrolled = false; // Ensure only one scroll per click

      this.isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');

      if (this.isSafari) {
        this.sensitivity = 1.5; // Adjust sensitivity for Safari
      } else {
        this.sensitivity = 1;
      }
      this.setupEventListeners();
      this.setupObservers();
      this.visibilityNavigation();
    }

    setupEventListeners() {
      this.left?.addEventListener('click', () => this.scrollLeft());
      this.right?.addEventListener('click', () => this.scrollRight());

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.scrollToSlide(index);
        });
      });

      if (this.thumbnails) {
        [...this.thumbnails.children].forEach((thumb, index) => {
          thumb.addEventListener("click", () => {
            this.scrollToSlide(index);
          });
        });
      }

      if (window.innerWidth >= 750) {
        // Pointer (Mouse & Touch) drag support
        this.sliderList.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.sliderList.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.sliderList.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.sliderList.addEventListener('pointerleave', this.onPointerUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });
      }
    }

    onPointerDown(event) {
      this.isPointerDown = true;
      this.startX = event.pageX;
      this.scrollLeftStart = this.sliderList.scrollLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.hasScrolled = false; // Allow scrolling again
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
      event.preventDefault(); // Prevent unintended selection
    }

    onPointerMove(event) {
      if (!this.isPointerDown || this.hasScrolled) return;
      const moveX = (event.pageX - this.startX) * this.sensitivity;

      if (Math.abs(moveX) > 2) { // Reduced sensitivity for quicker response
        this.dragged = true;
      }

      if (Math.abs(moveX) > 20) { // Set threshold for one-time scroll
        if (moveX > 0) {
          this.scrollLeft();
        } else {
          this.scrollRight();
        }
        this.hasScrolled = true; // Prevent further scrolling until next click
      }
    }

    onPointerUp() {
      this.isPointerDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollLeft() {
      const slideWidth = this.slide.clientWidth;
      this.sliderList.scrollLeft -= slideWidth;
    }

    scrollRight() {
      const slideWidth = this.slide.clientWidth;
      this.sliderList.scrollLeft += slideWidth;
    }

    scrollToSlide(index) {
      if (index < 0 || index >= this.sliderList.children.length) return;
      const slideWidth = this.slide.clientWidth;
      this.sliderList.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
    }

    setupObservers() {
      let slidesPerView;
      // Calculate the rootMargin to only show the current slide based on slidesPerView
      let rootMargin = "0px 0px 0px 0px";

      if (slidesPerView > 1) {
        // Calculate the percentage to hide based on slides per view
        // For 3 slides per view, we want to hide 66.66% from the right
        const hidePercentage = ((slidesPerView - 1) / slidesPerView) * 100;
        rootMargin = `0px -${hidePercentage}% 0px 0px`;
      }

      const sliderScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              slidesPerView = this.getSlidesPerView();
              this.currentIndex = parseInt(entry.target.dataset.index, 10);
              this.visibilityNavigation();
              this.visibilityDots();
              this.setCurrentNumberOfSlide();
              this.scrollThumbnailIntoView();
            }
          });
        },
        {
          root: this.sliderList,
          rootMargin: rootMargin,
          threshold: 0.3
        }
      );

      // Assign `data-index` to each slide for identification
      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }

    // Method to scroll thumbnails if the current index is out of view
    scrollThumbnailIntoView() {
      const container = this.thumbnails;

      // Check if thumbnails container exists
      if (!container) return;

      const thumbnails = [...container.children];
      const currentThumb = thumbnails[this.currentIndex];

      if (!currentThumb) return; // Safety check

      const containerTop = container.scrollTop;
      const containerBottom = containerTop;
      const thumbTop = currentThumb.offsetTop - container.offsetTop;
      const thumbBottom = thumbTop + currentThumb.clientHeight + currentThumb.clientHeight;

      // Scroll up if the thumbnail is above the visible area
      if (thumbTop < containerTop) {
        container.scrollTo({
          top: thumbTop,
          behavior: "smooth",
        });
      }
      // Scroll down if the thumbnail is below the visible area
      else if (thumbBottom > containerBottom) {
        container.scrollTo({
          top: thumbBottom - container.clientHeight,
          behavior: "smooth",
        });
      }
    }

    visibilityNavigation() {
      const size = this.sliderList.children.length - 1;
      const hasOneButton = this.hasAttribute('data-one-button');

      if (hasOneButton) {
        const totalSlides = this.sliderList.children.length;
        const slidesPerView = this.getSlidesPerView();

        // If all slides are visible at once, hide all buttons
        if (totalSlides <= slidesPerView) {
          this.buttons?.setAttribute('data-active', 'false');
          return;
        }

        // One button mode: only one button active at a time
        if (this.currentIndex === 0) {
          // At first slide: only right button active
          this.left?.setAttribute('data-active', 'false');
          this.right?.setAttribute('data-active', 'true');
        } else if (this.currentIndex === size) {
          // At last slide: only left button active
          this.left?.setAttribute('data-active', 'true');
          this.right?.setAttribute('data-active', 'false');
        } else {
          return; // In between slides, no buttons active
        }
      } else {
        // Default behavior: show/hide buttons based on position
        if (this.currentIndex === 0) {
          this.left?.setAttribute('data-active', 'false');
        } else {
          this.left?.setAttribute('data-active', 'true');
        }

        if (this.currentIndex === size) {
          this.right?.setAttribute('data-active', 'false');
        } else {
          this.right?.setAttribute('data-active', 'true');
        }
      }
    }

    visibilityDots() {
      this.dots.forEach((dot) => dot?.removeAttribute('data-active'));
      this.dots[this.currentIndex]?.setAttribute('data-active', '');
    }

    setCurrentNumberOfSlide() {
      if (this.number) {
        this.number.textContent = this.currentIndex + 1;
      }
    }

    getSlidesPerView() {
      const sliderContainer = this.sliderList; // Assuming this.sliderList is the container
      const slides = [...this.sliderList.children]; // Get all slides

      if (!sliderContainer || slides.length === 0) return 1; // Fallback to 1 if no slides found

      const containerWidth = sliderContainer.clientWidth; // Total width of slider container
      const slideWidth = slides[0].clientWidth; // Width of a single slide

      return Math.max(1, Math.floor(containerWidth / slideWidth)); // Ensure at least 1 slide per view
    }
  }

  customElements.define('simple-slider', SimpleSlider);
}
